import { useCallback, useEffect, useState } from 'react';
import { Tooltip } from 'antd';
import SystemHeader from '@/widgets/layout/SystemHeader';
import { useAppNavigate, useBackToParent, useSession, usePersistentPageSize } from '@/hooks';
import { usePermission } from '@/hooks/usePermission';
import { downloadBlob } from '@/utils/download';
import { importBatchesApi } from '@/services/importBatches/api';
import { isHandledAuthError } from '@/services/client';
import ConfirmDialog from '@/widgets/common/ConfirmDialog';
import { useToast } from '@/widgets/common';
import type { ImportBatchResponse } from '@/services/importBatches/types';
import useSearchPersistence from '@/hooks/useSearchPersistence';
import { useColumnVisibility } from '@/hooks/useColumnVisibility';
import { Pagination, BackButton, SearchCard, ResultCard, ResizeHandle, SortableHeader, TableLoadingRow, ArrowRightIcon, FileDocIcon, DemoInput, DemoSelect } from '@/widgets/demo/ListPrimitives';
import { ColumnVisibilityControl } from '@/widgets/demo/ColumnVisibilityControl';
import { demoButtonClass, computePagination, useResizableColumns, useSortState, type ColumnWidthMap, type SortOrder } from '@/widgets/demo/listHelpers';
const OCR_READ_LIST_COLUMN_DEFAULTS: ColumnWidthMap = {
  importNo: 120,
  fileName: 200,
  importType: 80,
  createdAt: 136,
  completedAt: 136,
  totalCount: 76,
  errorCount: 76,
  status: 108,
  registrant: 80,
  updater: 80,
  action: 200,
};

const OCR_READ_LIST_COLUMN_ORDER = [
  'importNo',
  'fileName',
  'importType',
  'createdAt',
  'completedAt',
  'totalCount',
  'errorCount',
  'status',
  'registrant',
  'updater',
  'action',
] as const;

// 列表示設定（No.30 横展開）用の画面キーと列ラベル。ヘッダ表示テキストと揃える。
const OCR_READ_LIST_COLUMN_VISIBILITY_KEY = 'ocr-read-list';

// 主キー相当の取込番号(importNo)・操作列(action)は常に表示。select 列は本画面に無い。
const OCR_READ_LIST_ALWAYS_VISIBLE_COLUMNS = ['importNo', 'action'];

const OCR_READ_LIST_COLUMN_LABELS: Record<(typeof OCR_READ_LIST_COLUMN_ORDER)[number], string> = {
  importNo: '取込番号',
  fileName: 'ファイル名',
  importType: '取込タイプ',
  createdAt: '取込日時',
  completedAt: '処理完了日時',
  totalCount: '合計件数',
  errorCount: 'エラー数',
  status: '取込ステータス',
  registrant: '登録者',
  updater: '更新者',
  action: '操作',
};

const OCR_READ_LIST_COLUMN_VISIBILITY_ITEMS = OCR_READ_LIST_COLUMN_ORDER.map((id) => ({
  id,
  label: OCR_READ_LIST_COLUMN_LABELS[id],
}));

interface ImportBatchRow {
  id: number;
  importNo: string;
  fileName: string | null;
  importType: string;
  /** OCR文書種別（PROTECTION_LEDGER / MOUSHIDESHO）。表示専用で判定ロジックには使わない。 */
  ocrDocType?: string;
  importAt: string;
  completedAt: string;
  total: number;
  errorCount: number;
  status: string;
  registeredBy: string;
  updatedBy: string;
  errorMessage: string | null;
  deleted: boolean;
  canDelete: boolean;
}

interface ImportListFilter {
  importNo: string;
  importType: string;
  status: string;
  dateFrom: string;
  dateTo: string;
  includeDeleted: boolean;
}

const DEFAULT_FILTER: ImportListFilter = {
  importNo: '',
  importType: '全て',
  status: '全て',
  dateFrom: '',
  dateTo: '',
  includeDeleted: false,
};

// 既存の絞り込み条件（key='ocr-read-list'）に加えて、現在ページとソートも保持する（顧客要望：
// 「検索条件は最後に選択した結果を保持する」）。絞り込み条件の型・runSearch/runClear のロジックを
// 変更せずに済むよう、ページ＋ソートは専用キー 'ocr-read-list:view' に分離して保持する。
interface ImportListView {
  page: number;
  sortBy: string | null;
  sortOrder: SortOrder;
}

const DEFAULT_VIEW: ImportListView = {
  page: 1,
  sortBy: null,
  sortOrder: null,
};

const STATUS_API: Record<string, string | undefined> = {
  全て: undefined,
  完了: 'COMPLETED',
  処理中: 'IMPORTING',
  エラー: 'ERROR',
  確認済: 'CONFIRMED',
  OCR解析中: 'OCR_PROCESSING',
  OCR解析完了: 'OCR_PARSED',
  OCR解析失敗: 'OCR_FAILED',
};

const IMPORT_TYPE_API: Record<string, { importType?: string; ocrDocType?: string }> = {
  全て: {},
  CSV: { importType: 'CSV' },
  OCR: { importType: 'OCR' }, // 旧・保存済フィルタとの後方互換（全OCR）。ドロップダウンの選択肢からは外す。
  'OCR（申出書）': { importType: 'OCR', ocrDocType: 'MOUSHIDESHO' },
  'OCR（保護台帳）': { importType: 'OCR', ocrDocType: 'PROTECTION_LEDGER' },
  手動入力: { importType: 'MANUAL' },
};

function formatDt(value: string | null | undefined): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  const pad = (number: number) => String(number).padStart(2, '0');
  return `${date.getFullYear()}/${pad(date.getMonth() + 1)}/${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
}

function toDateInputValue(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

function getDefaultDateRange(): { from: string; to: string } {
  const today = new Date();
  const from = new Date(today);
  from.setMonth(from.getMonth() - 1);
  return { from: toDateInputValue(from), to: toDateInputValue(today) };
}

function mapRow(batch: ImportBatchResponse): ImportBatchRow {
  return {
    id: batch.id,
    importNo: batch.importNo,
    fileName: batch.fileName,
    importType: batch.importType,
    ocrDocType: batch.ocrDocType,
    importAt: formatDt(batch.importedAt),
    completedAt: batch.completedAt ? formatDt(batch.completedAt) : '—',
    total: batch.totalCount,
    errorCount: batch.errorCount,
    status: batch.status,
    registeredBy: batch.registeredBy ?? '—',
    updatedBy: batch.updatedBy ?? '—',
    errorMessage: batch.errorMessage ?? null,
    deleted: batch.deleted ?? false,
    canDelete: batch.canDelete !== false,
  };
}

function displayImportType(importType: string, ocrDocType?: string): string {
  if (importType === 'MANUAL') return '手動入力';
  // OCR は文書種別（保護台帳/申出書）を併記する。判定ロジック（importType === 'OCR'）には影響させない。
  if (importType === 'OCR') return ocrDocType === 'MOUSHIDESHO' ? 'OCR（申出書）' : 'OCR（保護台帳）';
  return importType;
}

function displayStatus(status: string, importType?: string): string {
  // OCR バッチは「取込中」表記、CSV/手動入力は既存の「処理中」表記を維持する。
  if (importType === 'OCR') {
    if (status === 'IMPORTING' || status === '取込中') return '取込中';
    // OCR解析中 / OCR解析完了 / OCR解析失敗 / 完了 はそのまま
    return status;
  }
  // CSV / MANUAL 既存挙動（変更しない）
  if (status === 'IMPORTING') return '処理中';
  if (status === 'COMPLETED') return '完了';
  if (status === 'ERROR') return 'エラー';
  if (status === 'CONFIRMED') return '確認済';
  if (status === '取込中') return '処理中';
  if (status === 'エラー') return 'エラー';
  return status;
}

function statusBadgeClass(status: string, importType?: string): string {
  const display = displayStatus(status, importType);
  if (display === '完了') return 'bg-green-100 text-green-800';
  if (display === '処理中' || display === '取込中' || display === 'OCR解析中') return 'bg-blue-100 text-blue-800';
  if (display === 'エラー' || display === 'OCR解析失敗') return 'bg-red-100 text-red-800';
  if (display === '確認済') return 'bg-emerald-100 text-emerald-900';
  if (display === 'OCR解析完了') return 'bg-amber-100 text-amber-800';
  return 'bg-gray-100 text-gray-700';
}

function formatImportErrorDisplay(row: ImportBatchRow): string {
  if (row.importType === 'OCR') return '—';
  return typeof row.errorCount === 'number' ? row.errorCount.toLocaleString() : String(row.errorCount);
}

function ocrReviewActionLabel(row: ImportBatchRow): '確認' | '詳細' | null {
  if (row.importType !== 'OCR') return null;
  if (row.status === 'OCR解析完了') return '確認';
  if (row.status === '完了' || row.status === '確認済') return '詳細';
  return null;
}

const OcrReadList = () => {
  const navigate = useAppNavigate();
  const goBackToParent = useBackToParent();
  const toast = useToast();
  const { hasPermission } = usePermission();
  const session = useSession();
  const canDeleteBatch = hasPermission('DATA_IMPORT_HISTORY_DELETE');
  const canUseOcr = session.ocrUse && hasPermission('DATA_IMPORT_OCR');

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingDeleteRow, setPendingDeleteRow] = useState<ImportBatchRow | null>(null);

  const [savedFilter, setSavedFilter] = useSearchPersistence<ImportListFilter>('ocr-read-list', DEFAULT_FILTER);
  const [savedView, setSavedView] = useSearchPersistence<ImportListView>('ocr-read-list:view', DEFAULT_VIEW);

  const [qImportNo, setQImportNo] = useState(savedFilter.importNo);
  const [qImportType, setQImportType] = useState(savedFilter.importType);
  const [qStatus, setQStatus] = useState(savedFilter.status);
  const [qDateFrom, setQDateFrom] = useState(savedFilter.dateFrom || getDefaultDateRange().from);
  const [qDateTo, setQDateTo] = useState(savedFilter.dateTo || getDefaultDateRange().to);
  const [qIncludeDeleted, setQIncludeDeleted] = useState(savedFilter.includeDeleted ?? true);

  const [applied, setApplied] = useState<ImportListFilter>(savedFilter);
  const [currentPage, setCurrentPage] = useState(savedView.page ?? 1);
  const [pageSize, setPageSize] = usePersistentPageSize('ocr-read-list');
  const [rows, setRows] = useState<ImportBatchRow[]>([]);
  const [totalElements, setTotalElements] = useState(0);
  const [loading, setLoading] = useState(false);
  const { sort, setSort, sortQuery } = useSortState(
    savedView.sortBy ? { sortBy: savedView.sortBy, order: savedView.sortOrder } : undefined,
  );
  const { widths: colWidths, startResize } = useResizableColumns({ defaults: OCR_READ_LIST_COLUMN_DEFAULTS });

  // 列表示設定（No.30 横展開）。表示中の列だけに絞った順序配列を導出し、colgroup/thead/tbody/幅計算を統一する。
  const { isVisible: isColVisible, setVisible: setColVisible, reset: resetCols, hiddenCount: hiddenColCount } =
    useColumnVisibility(OCR_READ_LIST_COLUMN_VISIBILITY_KEY, OCR_READ_LIST_COLUMN_ORDER, {
      alwaysVisible: OCR_READ_LIST_ALWAYS_VISIBLE_COLUMNS,
    });
  const visibleColumnOrder = OCR_READ_LIST_COLUMN_ORDER.filter((id) => isColVisible(id));
  // 隠した列は colWidths から除外して合計幅を出す（隙間/幅ズレ防止）。
  const tableWidth = visibleColumnOrder.reduce((sum, id) => sum + (colWidths[id] ?? 0), 0);
  // tbody の空行/ローディング行 colSpan は「表示中の列数 + 末尾の余白 col(1)」。
  const visibleColSpan = visibleColumnOrder.length + 1;
  const handleSortChange = (next: typeof sort) => {
    setSort(next);
    setCurrentPage(1);
    setSavedView({ page: 1, sortBy: next.sortBy, sortOrder: next.order });
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await importBatchesApi.list({
        importNo: applied.importNo.trim() || undefined,
        importType: IMPORT_TYPE_API[applied.importType]?.importType,
        ocrDocType: IMPORT_TYPE_API[applied.importType]?.ocrDocType,
        status: STATUS_API[applied.status],
        dateFrom: applied.dateFrom ? `${applied.dateFrom}T00:00:00` : undefined,
        dateTo: applied.dateTo ? `${applied.dateTo}T23:59:59` : undefined,
        excludeDeleted: applied.includeDeleted ? undefined : true,
        ...sortQuery,
        page: currentPage - 1,
        size: pageSize,
      });
      // 取得ページが範囲外（永続化された currentPage が絞り込み後の総ページ数を超える等）で
      // 0件返却された場合、該当データはあるのに「データなし」表示になる。最終ページへ寄せて再取得する。
      if (data.content.length === 0 && data.totalElements > 0 && currentPage > 1) {
        setTotalElements(data.totalElements);
        setCurrentPage(Math.max(1, Math.ceil(data.totalElements / pageSize)));
        return;
      }
      setRows(data.content.map(mapRow));
      setTotalElements(data.totalElements);
    } catch (error) {
      setRows([]);
      setTotalElements(0);
      if (isHandledAuthError(error)) return;
    } finally {
      setLoading(false);
    }
  }, [applied, currentPage, pageSize, sortQuery]);

  useEffect(() => {
    // 検索条件・ページ変更に応じて一覧を取得する正当なデータ取得 effect。
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void load();
  }, [load]);

  const runSearch = () => {
    setLoading(true);
    setCurrentPage(1);
    const searchValues: ImportListFilter = {
      importNo: qImportNo,
      importType: qImportType,
      status: qStatus,
      dateFrom: qDateFrom,
      dateTo: qDateTo,
      includeDeleted: qIncludeDeleted,
    };
    setApplied(searchValues);
    setSavedFilter(searchValues);
    setSavedView({ page: 1, sortBy: sort.sortBy, sortOrder: sort.order });
  };

  const runClear = () => {
    setLoading(true);
    const { from, to } = getDefaultDateRange();
    setQImportNo('');
    setQImportType('全て');
    setQStatus('全て');
    setQDateFrom(from);
    setQDateTo(to);
    setQIncludeDeleted(true);
    setCurrentPage(1);
    const defaultFilter: ImportListFilter = {
      importNo: '',
      importType: '全て',
      status: '全て',
      dateFrom: from,
      dateTo: to,
      includeDeleted: true,
    };
    setApplied(defaultFilter);
    setSavedFilter(defaultFilter);
    setSavedView({ page: 1, sortBy: sort.sortBy, sortOrder: sort.order });
  };

  const { totalPages, safePage, rangeFrom, rangeTo } = computePagination(currentPage, totalElements, pageSize);

  const deleteBatch = async (row: ImportBatchRow) => {
    setPendingDeleteRow(row);
    setConfirmOpen(true);
  };

  const downloadFile = async (row: ImportBatchRow) => {
    try {
      const res = await importBatchesApi.download(row.id);
      if (row.importType === 'OCR') {
        const disposition = res.headers?.['content-disposition'] ?? '';
        const match = disposition.match(/filename\*?=(?:UTF-8'')?([^;\s]+)/i);
        const serverName = match ? decodeURIComponent(match[1]) : null;
        const fallback = `${row.importNo}.csv`;
        const filename = serverName || fallback;
        const contentType = (res.headers?.['content-type'] as string) || 'text/csv;charset=utf-8';
        downloadBlob(new Blob([res.data], { type: contentType }), filename);
      } else {
        const filename = row.errorCount > 0
          ? `${row.importNo}_errors.csv`
          : `${row.importNo}.csv`;
        downloadBlob(new Blob([res.data], { type: 'text/csv;charset=utf-8' }), filename);
      }
    } catch (error) {
      if (isHandledAuthError(error)) return;
    }
  };

  const executeDelete = async () => {
    if (!pendingDeleteRow) return;
    setConfirmOpen(false);
    try {
      await importBatchesApi.delete(pendingDeleteRow.id);
      await load();
    } catch (error) {
      if (isHandledAuthError(error)) return;
    } finally {
      setPendingDeleteRow(null);
    }
  };

  const openOcrReview = async (row: ImportBatchRow) => {
    try {
      const { data } = await importBatchesApi.listOcrCases(row.id);
      if (!data || data.length === 0) {
        toast.warning('この OCR バッチには確認可能な案件がまだありません。');
        return;
      }
      const firstCaseId = data[0].id;
      navigate('application-review', {
        caseIndex: 0,
        importBatchId: row.id,
        ocrCaseId: firstCaseId,
      });
    } catch (error) {
      if (isHandledAuthError(error)) return;
    }
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <SystemHeader breadcrumbs={[{ label: 'データ取込' }]} />
      <main className="mx-auto w-full max-w-[1600px] px-6 py-8">
        <BackButton onClick={goBackToParent} />

        <div className="mb-4">
          <h2 className="text-xl font-bold text-gray-800">データ取込</h2>
          <p className="mt-1 text-sm text-gray-500">データ取込の実行と、取り込んだデータの確認画面へ遷移できます。</p>
        </div>

        <SearchCard className="mb-4">
          <div className="mb-4 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-5">
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">取込番号</label>
              <DemoInput
                type="text"
                value={qImportNo}
                onChange={(event) => setQImportNo(event.target.value)}
                placeholder="例: IMP-2024-0042"
                className="w-full"
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">取込タイプ</label>
              <DemoSelect
                value={qImportType}
                onChange={(event) => setQImportType(event.target.value)}
                className="w-full"
              >
                <option value="全て">全て</option>
                <option>CSV</option>
                <option>OCR（申出書）</option>
                <option>OCR（保護台帳）</option>
                <option>手動入力</option>
              </DemoSelect>
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">取込日（From）</label>
              <DemoInput
                type="date"
                value={qDateFrom}
                onChange={(event) => setQDateFrom(event.target.value)}
                className="w-full"
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">取込日（To）</label>
              <DemoInput
                type="date"
                value={qDateTo}
                onChange={(event) => setQDateTo(event.target.value)}
                className="w-full"
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">取込ステータス</label>
              <DemoSelect
                value={qStatus}
                onChange={(event) => setQStatus(event.target.value)}
                className="w-full"
              >
                <option value="全て">全て</option>
                <option>完了</option>
                <option>処理中</option>
                <option>エラー</option>
              </DemoSelect>
            </div>
          </div>
          <div className="flex items-center justify-between">
            <label className="flex items-center gap-2 text-sm text-gray-700 select-none">
              <input
                type="checkbox"
                checked={qIncludeDeleted}
                onChange={(e) => setQIncludeDeleted(e.target.checked)}
                className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              削除済みを含む
            </label>
            <div className="flex gap-3">
              <button type="button" onClick={runClear} className={demoButtonClass.secondary}>クリア</button>
              <button type="button" onClick={runSearch} className={demoButtonClass.primary}>検索</button>
            </div>
          </div>
        </SearchCard>

        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-3">
            <button type="button" onClick={() => navigate('data-import')} className={demoButtonClass.slate}>
              <ArrowRightIcon />
              CSV取込
            </button>
            {canUseOcr && (
              <button type="button" onClick={() => navigate('ocr-read-import')} className={`inline-flex items-center ${demoButtonClass.orange}`}>
                <FileDocIcon />
                OCR読取
              </button>
            )}
            <button type="button" onClick={() => navigate('recipient-new', { baseCrumb: { label: 'データ取込', page: 'ocr-read-list' } })} className={demoButtonClass.success}>
              新規受給者作成
            </button>
          </div>
        </div>

        <ResultCard
          title={<h3 className="text-lg font-bold text-gray-800">取込履歴</h3>}
          extra={
            <>
              <span className="text-sm text-gray-500">検索結果 {totalElements} 件</span>
              <ColumnVisibilityControl
                columns={OCR_READ_LIST_COLUMN_VISIBILITY_ITEMS}
                isVisible={isColVisible}
                setVisible={setColVisible}
                onReset={resetCols}
                alwaysVisible={OCR_READ_LIST_ALWAYS_VISIBLE_COLUMNS}
                hiddenCount={hiddenColCount}
              />
            </>
          }
        >
          <div className="overflow-x-auto">
            <table className="min-w-full table-fixed" style={{ width: `${tableWidth}px` }}>
              <colgroup>
                {visibleColumnOrder.map((id) => (
                  <col key={id} style={{ width: `${colWidths[id]}px` }} />
                ))}
                <col />
              </colgroup>
              <thead className="border-b-2 border-gray-200 bg-gray-50 whitespace-nowrap">
                <tr>
                  {isColVisible('importNo') && (
                    <SortableHeader field="importNo" label="取込番号" sort={sort} onSortChange={handleSortChange} className="px-2 py-3 text-sm font-medium text-gray-700 normal-case tracking-normal" trailing={<ResizeHandle columnId="importNo" onMouseDown={startResize} />} />
                  )}
                  {isColVisible('fileName') && (
                    <SortableHeader field="fileName" label="ファイル名" sort={sort} onSortChange={handleSortChange} className="px-2 py-3 text-sm font-medium text-gray-700 normal-case tracking-normal" trailing={<ResizeHandle columnId="fileName" onMouseDown={startResize} />} />
                  )}
                  {isColVisible('importType') && (
                    <SortableHeader field="importType" label="取込タイプ" sort={sort} onSortChange={handleSortChange} className="px-2 py-3 text-sm font-medium text-gray-700 normal-case tracking-normal" trailing={<ResizeHandle columnId="importType" onMouseDown={startResize} />} />
                  )}
                  {isColVisible('createdAt') && (
                    <SortableHeader field="createdAt" label="取込日時" sort={sort} onSortChange={handleSortChange} className="px-2 py-3 text-sm font-medium text-gray-700 normal-case tracking-normal" trailing={<ResizeHandle columnId="createdAt" onMouseDown={startResize} />} />
                  )}
                  {isColVisible('completedAt') && (
                    <th className="relative px-2 py-3 text-left text-sm font-medium text-gray-700">
                      処理完了日時
                      <ResizeHandle columnId="completedAt" onMouseDown={startResize} />
                    </th>
                  )}
                  {isColVisible('totalCount') && (
                    <SortableHeader field="totalCount" label="合計件数" sort={sort} onSortChange={handleSortChange} className="px-2 py-3 text-sm font-medium text-gray-700 normal-case tracking-normal" trailing={<ResizeHandle columnId="totalCount" onMouseDown={startResize} />} />
                  )}
                  {isColVisible('errorCount') && (
                    <SortableHeader field="errorCount" label="エラー数" sort={sort} onSortChange={handleSortChange} className="px-2 py-3 text-sm font-medium text-gray-700 normal-case tracking-normal" trailing={<ResizeHandle columnId="errorCount" onMouseDown={startResize} />} />
                  )}
                  {isColVisible('status') && (
                    <SortableHeader field="status" label="取込ステータス" sort={sort} onSortChange={handleSortChange} className="px-2 py-3 text-sm font-medium text-gray-700 normal-case tracking-normal" trailing={<ResizeHandle columnId="status" onMouseDown={startResize} />} />
                  )}
                  {isColVisible('registrant') && (
                    <th className="relative px-2 py-3 text-left text-sm font-medium text-gray-700">
                      登録者
                      <ResizeHandle columnId="registrant" onMouseDown={startResize} />
                    </th>
                  )}
                  {isColVisible('updater') && (
                    <th className="relative px-2 py-3 text-left text-sm font-medium text-gray-700">
                      更新者
                      <ResizeHandle columnId="updater" onMouseDown={startResize} />
                    </th>
                  )}
                  {isColVisible('action') && (
                    <th className="px-2 py-3 text-left text-sm font-medium text-gray-700">操作</th>
                  )}
                  <th aria-hidden="true" />
                </tr>
              </thead>
              <tbody className={`divide-y divide-gray-200 transition-opacity ${loading ? 'opacity-60' : ''}`}>
                {rows.length === 0 ? (
                  loading ? (
                    <TableLoadingRow colSpan={visibleColSpan} />
                  ) : (
                    <tr>
                      <td colSpan={visibleColSpan} className="px-4 py-8 text-center text-sm text-gray-500">
                        該当するデータがありません
                      </td>
                    </tr>
                  )
                ) : (
                  rows.map((row) => (
                    <tr key={row.id} className={row.deleted ? 'bg-gray-200 opacity-60' : 'transition hover:bg-blue-50'}>
                      {isColVisible('importNo') && (
                        <td className="px-2 py-3 text-sm font-medium">
                          <Tooltip title={row.importNo} placement="topLeft">
                            <span className="block truncate">{row.importNo}</span>
                          </Tooltip>
                        </td>
                      )}
                      {isColVisible('fileName') && (
                        <td className="px-2 py-3 text-sm">
                          <Tooltip title={row.fileName ?? '—'} placement="topLeft">
                            <span className="block truncate">{row.fileName ?? '—'}</span>
                          </Tooltip>
                        </td>
                      )}
                      {isColVisible('importType') && (
                        <td className="px-2 py-3 text-sm">
                          <Tooltip title={displayImportType(row.importType, row.ocrDocType)} placement="topLeft">
                            <span className="block truncate">{displayImportType(row.importType, row.ocrDocType)}</span>
                          </Tooltip>
                        </td>
                      )}
                      {isColVisible('createdAt') && (
                        <td className="px-2 py-3 text-sm">
                          <Tooltip title={row.importAt} placement="topLeft">
                            <span className="block truncate">{row.importAt}</span>
                          </Tooltip>
                        </td>
                      )}
                      {isColVisible('completedAt') && (
                        <td className="px-2 py-3 text-sm">
                          <Tooltip title={row.completedAt} placement="topLeft">
                            <span className="block truncate">{row.completedAt}</span>
                          </Tooltip>
                        </td>
                      )}
                      {isColVisible('totalCount') && (
                        <td className="px-2 py-3 text-left text-sm tabular-nums truncate">{row.total.toLocaleString()}</td>
                      )}
                      {isColVisible('errorCount') && (
                        <td className="px-2 py-3 text-left text-sm tabular-nums text-gray-700 truncate">{formatImportErrorDisplay(row)}</td>
                      )}
                      {isColVisible('status') && (
                        <td className="px-2 py-3 truncate">
                          {row.errorMessage ? (
                            <Tooltip title={row.errorMessage} placement="topLeft">
                              <span className={`rounded-full px-2 py-1 text-xs cursor-help ${statusBadgeClass(row.status, row.importType)}`}>
                                {displayStatus(row.status, row.importType)}
                              </span>
                            </Tooltip>
                          ) : (
                            <span className={`rounded-full px-2 py-1 text-xs ${statusBadgeClass(row.status, row.importType)}`}>
                              {displayStatus(row.status, row.importType)}
                            </span>
                          )}
                        </td>
                      )}
                      {isColVisible('registrant') && (
                        <td className="px-2 py-3 text-sm">
                          <Tooltip title={row.registeredBy} placement="topLeft">
                            <span className="block truncate">{row.registeredBy}</span>
                          </Tooltip>
                        </td>
                      )}
                      {isColVisible('updater') && (
                        <td className="px-2 py-3 text-sm">
                          <Tooltip title={row.updatedBy} placement="topLeft">
                            <span className="block truncate">{row.updatedBy}</span>
                          </Tooltip>
                        </td>
                      )}
                      {isColVisible('action') && (
                      <td className="whitespace-nowrap px-2 py-3 text-left align-middle">
                        {row.deleted ? (
                          <span className="text-xs text-gray-400">削除済み</span>
                        ) : row.status === '取込中' || row.status === 'OCR解析中' ? null : (
                          <div className="inline-flex items-center gap-x-3">
                            {row.errorCount > 0 && row.importType !== 'OCR' ? (
                              <button
                                type="button"
                                onClick={() => navigate('import-error-edit', { batchId: row.id })}
                                className={demoButtonClass.linkOrange}
                              >
                                エラー修正
                              </button>
                            ) : null}
                            {ocrReviewActionLabel(row) ? (
                              <button
                                type="button"
                                onClick={() => void openOcrReview(row)}
                                className={demoButtonClass.linkBlue}
                              >
                                {ocrReviewActionLabel(row)}
                              </button>
                            ) : null}
                            {canDeleteBatch && row.canDelete && (
                              <button
                                type="button"
                                onClick={() => void deleteBatch(row)}
                                className={demoButtonClass.linkRed}
                              >
                                削除
                              </button>
                            )}
                            {row.importType !== 'MANUAL' ? (
                              <button
                                type="button"
                                onClick={() => void downloadFile(row)}
                                className={demoButtonClass.linkSlate}
                              >
                                ダウンロード
                              </button>
                            ) : null}
                          </div>
                        )}
                      </td>
                      )}
                      <td aria-hidden="true" />
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <Pagination
            currentPage={safePage}
            totalPages={totalPages}
            total={totalElements}
            rangeFrom={rangeFrom}
            rangeTo={rangeTo}
            onPageChange={(next) => { setLoading(true); setCurrentPage(next); setSavedView({ page: next, sortBy: sort.sortBy, sortOrder: sort.order }); }}
            pageSize={pageSize}
            onPageSizeChange={(next) => { setLoading(true); setPageSize(next); setCurrentPage(1); setSavedView({ page: 1, sortBy: sort.sortBy, sortOrder: sort.order }); }}
          />
        </ResultCard>
      </main>
      <ConfirmDialog
        open={confirmOpen}
        message={`${pendingDeleteRow?.importNo ?? ''} を削除します。\n削除すると受給者情報並びに支給者情報からも削除されます。\nよろしいですか？`}
        variant="danger"
        onConfirm={() => void executeDelete()}
        onCancel={() => { setConfirmOpen(false); setPendingDeleteRow(null); }}
      />
    </div>
  );
};

export default OcrReadList;
